import { useDisclosure } from "@chakra-ui/react";
import moment from "moment";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppIcon from "../../components/Core/AppIcon/AppIcon";
import AppText, { FormLabel } from "../../components/Core/AppText/AppText";
import Button from "../../components/Core/Button/Button";
import Modal from "../../components/Core/Modal/Modal";
import SortableTh from "../../components/Core/SortableTh/SortableTh";
import TextArea from "../../components/Core/TextArea/TextArea";
import Flex from "../../components/Layout/Flex";
import VirtualizedTableLayout from "../../components/Layout/VirtualizedTableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import { useListFilters } from "../../components/Shared/Filters/useListFilters";
import ColumnResizeHandle from "../../components/Shared/ListColumns/ColumnResizeHandle";
import { ListColumn } from "../../components/Shared/ListColumns/types";
import { useColumnResize } from "../../components/Shared/ListColumns/useColumnResize";
import { useListColumns } from "../../components/Shared/ListColumns/useListColumns";
import ListPageToolbar from "../../components/Shared/ListPageToolbar/ListPageToolbar";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import ListViewTagChips, {
	hasListViewTypes,
} from "../../components/Shared/TagChip/ListViewTagChips";
import { JobBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { JOB_STATUS } from "../../configs/constants/JobStatus";
import { routes } from "../../configs/constants/routes";
import useDebounce from "../../hooks/useDebounce";
import useQueryState from "../../hooks/useQueryState";
import Job from "../../models/Job";
import {
	EMPTY_JOB_DEFINITION,
	JobFilterDefinition,
	definitionToJobParams,
	isEmptyJobDefinition,
	jobDefinitionsEqual,
} from "../../models/JobFilter";
import { ORDER_SORTING_COLUMN, SORT_ORDER } from "../../models/Sorting";
import { GetJobsParams, useJobsInfinite, useUpdateJob } from "../../networking/job.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { cn } from "../../utils/cn";
import { getCompetenceLabelByKey } from "../../utils/competence.utils";
import { formatNumber, jobFromTo } from "../../utils/date.time.utils";
import { jobRoute } from "../../utils/route.utils";
import { decimalToPercentage } from "../../utils/string.utils";
import JobFilterPanel from "./components/JobFilterPanel";
import { useJobFilterData } from "./components/useJobFilterData";

const DANGER_STATUSES = [JOB_STATUS.DECLINED, JOB_STATUS.DELETED, JOB_STATUS.CANCELLED];

const isDangerStatus = (status: JOB_STATUS) => DANGER_STATUSES.includes(status);

// Row background per status, matching the old list (TableLayout's
// success/warning/notice classes). Applied to the cells rather than the <tr>
// because VirtualizedTableLayout owns the row's background (odd/even + hover).
function statusRowBg(status: JOB_STATUS): string | undefined {
	if (status === JOB_STATUS.BOOKED) return "#bdecd1"; // COLOR_SUCCESS_LIGHT
	if (isDangerStatus(status)) return "#FFEBEB"; // COLOR_DANGER_LIGHT
	if (status === JOB_STATUS.BOOKED_WITH_UNMANNED_SHIFTS) return "#fffcdb";
	return undefined;
}

// A darker shade of each status background, used for the top/bottom border of a
// colored row so the colored block "pops" out from the neighbouring rows.
function statusRowBorder(status: JOB_STATUS): string | undefined {
	if (status === JOB_STATUS.BOOKED) return "#7cc69b"; // darker green
	if (isDangerStatus(status)) return "#f0b0b0"; // darker pink
	if (status === JOB_STATUS.BOOKED_WITH_UNMANNED_SHIFTS) return "#e3d98a"; // darker yellow
	return undefined;
}

/**
 * Jobs list rendered with the virtualized table layout (the /jobs page).
 *
 *  - Server-paged via `useJobsInfinite`; the active filter definition is
 *    translated to `GET /job` query params by `definitionToJobParams`.
 *  - Filtering (the active definition, org + personal saved filters) is owned by
 *    the shared `useListFilters` hook; unlike the consultants/customers list,
 *    filtering/search/sort all happen server-side. Column visibility and widths
 *    are owned by `useListColumns` — that works the same whether or not the full
 *    data set is loaded, since it only concerns which columns render.
 */
const JobListPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const filters = useListFilters<JobFilterDefinition>({
		filtersKey: "jobFilters",
		definitionsEqual: jobDefinitionsEqual,
		isEmptyDefinition: isEmptyJobDefinition,
		emptyDefinition: EMPTY_JOB_DEFINITION,
	});
	const {
		pool,
		setPool,
		searchTerm,
		definition,
		setDefinition,
		savedFilters,
		orgFilters,
		selectedSavedId,
		handleSaveNew,
		handleUpdateSaved,
		handleRenameSaved,
		handleDeleteSaved,
		handleReorderSaved,
	} = filters;

	// Sort lives in the URL (?col=&order=) and toggles on every header click.
	const [order, setOrder] = useQueryState<SORT_ORDER>("order", {
		initialValue: SORT_ORDER.ASC,
	});
	const [sort, setSort] = useQueryState<ORDER_SORTING_COLUMN>("col", {
		initialValue: ORDER_SORTING_COLUMN.CREATED,
	});

	const filterPanel = useDisclosure();
	const keyword = useDebounce(searchTerm, 350);

	const { options: filterOptions, isLoading: optionsLoading } = useJobFilterData(pool);

	// Translate the active definition to query params and fetch server-paged.
	// The Breadcrumbs pool dropdown is a top-level scope: when set it overrides
	// the filter's `pools` dimension. The query key includes every resolved param,
	// so changing filter/sort/keyword/pool resets the infinite list.
	const query: Omit<GetJobsParams, "page"> = {
		...definitionToJobParams(definition),
		pageSize: 30,
		keyword,
		sort,
		order,
		expand: ["applicationsCount", "customer"],
		...(pool ? { pool } : {}),
	};

	const { jobs, totalCount, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useJobsInfinite(query);

	const { data: organisation } = useOrganisation();
	const competenceLabelByKey = getCompetenceLabelByKey(organisation?.competences ?? []);

	// Job tag types opted in to list views (org settings) gate the "tags" column:
	// it only exists — and appears in the picker — when something is opted in.
	const jobTagTypes = organisation?.jobTagTypes;
	const tagsAvailable = hasListViewTypes(jobTagTypes);
	// Same gate for the customer's tags ("Kundtaggar"): the list expands the
	// customer, so the chips read the live tags off job.customer.
	const customerTagTypes = organisation?.customerTagTypes;
	const customerTagsAvailable = hasListViewTypes(customerTagTypes);
	// And for the matching gate ("Kompetenskrav"): required consultant tags and
	// certifications share the "Visa i listvyer" opt-in with the consultant list.
	const consultantTagTypes = organisation?.consultantTagTypes;
	const certificationTypes = organisation?.certificationTypes;
	const requirementsAvailable = hasListViewTypes(consultantTagTypes, certificationTypes);

	// Comment (internal notes) quick-edit: click a row's notes cell to edit.
	const [showCommentModal, setShowCommentModal] = useState<Job>();
	const [updatedComment, setUpdatedComment] = useState("");
	const commentRef = useRef<HTMLTextAreaElement>(null);
	const { mutate, isLoading: isUpdatingComment } = useUpdateJob();

	async function updateComment() {
		if (showCommentModal) {
			await mutate({ jobId: showCommentModal.id, update: { internalNotes: updatedComment } });
			setShowCommentModal(undefined);
		}
	}

	const updateFlag = useCallback(
		(id: string, newFlagStatus: boolean) => mutate({ jobId: id, update: { flag: newFlagStatus } }),
		[mutate]
	);

	const openComment = useCallback((job: Job) => {
		setShowCommentModal(job);
		setUpdatedComment(job.internalNotes || "");
		setTimeout(() => commentRef.current?.focus(), 5);
	}, []);

	// The declarative column set. `id` is mandatory; all others are toggleable and
	// default on (preserving the current view). Sortable columns carry a server
	// sort key; weights are relative and user-resizable.
	const columns = useMemo<ListColumn<Job>[]>(
		() => [
			{
				key: "id",
				label: "ID",
				weight: 6,
				mandatory: true,
				pinned: true,
				sortColumn: ORDER_SORTING_COLUMN.ORDER_NO,
				headerClassName: "pl-6",
				cellClassName: "pl-6",
				renderCell: (job) => (
					<div className="flex items-center">
						<div>
							{job.orderNumber}
							{job.customerOrderNumber && (
								<>
									<br />
									<AppText size="s" color="grayText" lineHeight={"95%"}>
										{job.customerOrderNumber}
									</AppText>
								</>
							)}
						</div>
						<AppIcon
							onClick={(e) => {
								e.stopPropagation();
								updateFlag(job.id, !job?.flag);
							}}
							icon={job?.flag ? "black-flag" : "white-flag"}
							size="15px"
						/>
					</div>
				),
			},
			{
				key: "fromTo",
				label: "FROM_TO",
				weight: 20,
				sortColumn: ORDER_SORTING_COLUMN.DATE,
				cellClassName: "whitespace-nowrap",
				cellTitle: (job) => jobFromTo(job.startDate as string, job.endDate as string),
				renderCell: (job) => jobFromTo(job.startDate as string, job.endDate as string),
			},
			{
				key: "hours",
				label: "HOURS_SHORT",
				weight: 6,
				sortColumn: ORDER_SORTING_COLUMN.HOURS,
				renderCell: (job) => formatNumber(job.noOfHours || 0, 0),
			},
			{
				key: "customer",
				label: "CUSTOMER",
				weight: 13,
				cellTitle: (job) => job.customer?.name,
				renderCell: (job) => job.customer?.name,
			},
			{
				key: "customerTags",
				label: "Kundtaggar",
				weight: 9,
				available: customerTagsAvailable,
				// Opt-in: offered in the picker but hidden until the user toggles it.
				defaultVisible: false,
				renderCell: (job) => (
					<ListViewTagChips tags={job.customer?.tags} tagTypes={customerTagTypes} />
				),
			},
			{
				key: "city",
				label: "CITY",
				weight: 7,
				sortColumn: ORDER_SORTING_COLUMN.CITY,
				cellTitle: (job) => job.address?.city,
				renderCell: (job) => job.address?.city,
			},
			{
				key: "service",
				label: "SERVICE",
				weight: 10,
				cellTitle: (job) => competenceLabelByKey.get(job.competence.service),
				renderCell: (job) => competenceLabelByKey.get(job.competence.service),
			},
			{
				key: "created",
				label: "CREATED",
				weight: 9,
				sortColumn: ORDER_SORTING_COLUMN.CREATED,
				renderCell: (job) => (
					<div className="flex items-center">
						{moment(job.metadata.created).format(DATE_TIME_FORMAT_TYPES.DATE)}
						<span className="ml-1 mt-[3px]">
							<AppIcon icon={job.hidden ? "hidden" : "visible"} size="16px" />
						</span>
					</div>
				),
			},
			{
				key: "status",
				label: "STATUS",
				weight: 6,
				renderCell: (job) => t(("job.jobStatus." + job.status.toLowerCase()) as any),
			},
			{
				key: "tg",
				label: "TG",
				weight: 4,
				renderCell: (job) => (job.price?.tg ? decimalToPercentage(job.price?.tg) : "-"),
			},
			{
				key: "interested",
				label: "INTERESTED",
				weight: 6,
				renderCell: (job) => job.applicationCount ?? 0,
			},
			{
				key: "tags",
				label: "Uppdragstaggar",
				weight: 9,
				available: tagsAvailable,
				renderCell: (job) => <ListViewTagChips tags={job.tags} tagTypes={jobTagTypes} />,
			},
			{
				key: "requirements",
				label: "Kompetenskrav",
				weight: 9,
				available: requirementsAvailable,
				// Opt-in: offered in the picker but hidden until the user toggles it.
				defaultVisible: false,
				renderCell: (job) => (
					<ListViewTagChips
						tags={job.requirements?.tags}
						tagTypes={consultantTagTypes}
						certifications={job.requirements?.certifications}
						certificationTypes={certificationTypes}
					/>
				),
			},
			{
				key: "notes",
				label: "NOTES",
				weight: 13,
				cellTitle: (job) => job.internalNotes || "",
				onCellClick: (job, e) => {
					e.stopPropagation();
					openComment(job);
				},
				renderCell: (job) => (
					<AppText size="s" italic lineClamp={1}>
						{job.internalNotes}
					</AppText>
				),
			},
		],
		[
			t,
			competenceLabelByKey,
			tagsAvailable,
			jobTagTypes,
			customerTagsAvailable,
			customerTagTypes,
			requirementsAvailable,
			consultantTagTypes,
			certificationTypes,
			updateFlag,
			openComment,
		]
	);

	const listColumns = useListColumns<Job>(
		"jobListColumns",
		columns,
		"jobListColumnWidths",
		"jobListColumnOrder"
	);
	const { visibleColumns } = listColumns;

	const resize = useColumnResize<Job>({
		columns: visibleColumns,
		onCommit: listColumns.setColumnWidths,
		onReset: listColumns.resetColumnWidth,
	});

	// Widths use the resize-aware weight so an in-progress drag previews live.
	const totalWeight = visibleColumns.reduce((sum, c) => sum + resize.weightOf(c.key), 0);
	const widthOf = (col: ListColumn<Job>) => `${(resize.weightOf(col.key) / totalWeight) * 100}%`;

	const handleSortColumn = (column: ORDER_SORTING_COLUMN) => {
		setOrder(order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC);
		setSort(column);
	};

	const onEndReached = useCallback(() => {
		if (hasNextPage && !isFetchingNextPage) fetchNextPage();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const renderTableHeader = useCallback(() => {
		return (
			<thead className="border-b-2 border-border">
				<tr className="text-left bg-muted text-sm font-medium whitespace-nowrap">
					{visibleColumns.map((col) => {
						const handle = resize.handleProps(col.key);
						return col.sortColumn ? (
							<SortableTh
								key={col.key}
								label={t(col.label as any)}
								column={col.sortColumn as ORDER_SORTING_COLUMN}
								activeColumn={sort}
								order={order}
								onSort={handleSortColumn}
								width={widthOf(col)}
								className={col.headerClassName}
								resizeHandle={handle && <ColumnResizeHandle {...handle} />}
							/>
						) : (
							<th
								key={col.key}
								className={cn("relative pt-3 pb-2 px-4", col.headerClassName)}
								style={{ width: widthOf(col) }}
							>
								{t(col.label as any)}
								{handle && <ColumnResizeHandle {...handle} />}
							</th>
						);
					})}
				</tr>
			</thead>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visibleColumns, sort, order, t, resize]);

	const renderTableRow = useCallback(
		(index: number) => {
			const job = jobs[index];
			if (!job) return null;

			// The status row background (matching the old list) is applied to each
			// cell, since the virtualized layout owns the <tr> background.
			const rowBg = statusRowBg(job.status);
			const isColored = !!rowBg;
			const prevJob = index > 0 ? jobs[index - 1] : undefined;
			const prevColored = !!(prevJob && statusRowBg(prevJob.status));
			const isLast = index === jobs.length - 1;

			// Borders are top-only (so they collapse — no double borders between
			// rows). The boundary above this row should be darkened to its colored
			// status shade when either this row or the row above is colored, framing
			// a colored block top & bottom. The last row, when colored, needs an
			// explicit bottom edge since there's no row below to draw it.
			//
			// On a shared boundary between two differently-colored rows, a danger
			// (declined/deleted/cancelled) row wins — its red edge takes precedence
			// over e.g. a neighbouring green row's border.
			const topBorderColor = (() => {
				const dangerHere = isColored && isDangerStatus(job.status);
				const dangerAbove = prevColored && prevJob && isDangerStatus(prevJob.status);
				if (dangerHere || dangerAbove) return statusRowBorder(JOB_STATUS.DECLINED);
				if (isColored) return statusRowBorder(job.status);
				if (prevColored && prevJob) return statusRowBorder(prevJob.status);
				return undefined; // falls back to the default class color (border token)
			})();

			// The first row never gets a top border (the header sits above it),
			// regardless of status.
			const showTopBorder = index !== 0;

			const cell = cn(
				"px-4 py-2 border-solid align-middle text-base truncate",
				// Default border matches the consultant/customer lists; colored rows
				// override the top color via `borderTopColor` below.
				showTopBorder && "border-t border-t-border",
				isColored && isLast && "border-b"
			);

			const rowCellStyle: React.CSSProperties = {
				backgroundColor: rowBg,
				...(topBorderColor ? { borderTopColor: topBorderColor } : {}),
				...(isColored && isLast ? { borderBottomColor: statusRowBorder(job.status) } : {}),
			};

			return (
				<>
					{visibleColumns.map((col) => {
						const extra =
							typeof col.cellClassName === "function" ? col.cellClassName(job) : col.cellClassName;
						const onClick = col.onCellClick
							? (e: React.MouseEvent) => col.onCellClick!(job, e)
							: col.stopClickPropagation
							? (e: React.MouseEvent) => e.stopPropagation()
							: undefined;
						return (
							<td
								key={col.key}
								className={cn(cell, extra)}
								style={{ ...rowCellStyle, width: widthOf(col) }}
								title={col.cellTitle?.(job)}
								onClick={onClick}
							>
								{col.renderCell(job)}
							</td>
						);
					})}
				</>
			);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[jobs, visibleColumns, resize]
	);

	return (
		<>
			<Breadcrumbs
				items={JobBreadcrumbs}
				onChangePool={setPool}
				selectedPool={pool}
				showPoolDropdown
			/>

			<ListPageToolbar
				filters={filters}
				count={totalCount}
				onOpenFilterPanel={filterPanel.onOpen}
				createTo={routes.JOB_CREATE}
				createLabel="CREATE_JOB"
				columns={listColumns}
			/>

			<JobFilterPanel
				isOpen={filterPanel.isOpen}
				onClose={filterPanel.onClose}
				definition={definition}
				onChange={setDefinition}
				options={filterOptions}
				savedFilters={savedFilters}
				orgFilters={orgFilters}
				activeSavedId={selectedSavedId}
				onSaveNew={handleSaveNew}
				onUpdateSaved={handleUpdateSaved}
				onRenameSaved={handleRenameSaved}
				onDeleteSaved={handleDeleteSaved}
				onReorderSaved={handleReorderSaved}
			/>

			{isLoading || optionsLoading ? (
				<LoadingIndicator className="mt-4" />
			) : jobs.length ? (
				<VirtualizedTableLayout
					itemCount={jobs.length}
					estimatedRowHeight={48}
					overscan={10}
					renderHeader={renderTableHeader}
					renderRow={renderTableRow}
					// data-job: right-clicking a row opens the global job context menu
					// (ContactContextMenu) — open/copy job, customer link, tag toggles.
					getRowProps={(index) =>
						({ "data-job": jobs[index]?.id } as React.HTMLAttributes<HTMLTableRowElement>)
					}
					onRowClick={(index) => navigate(jobRoute(jobs[index]?.id))}
					onEndReached={onEndReached}
					isFetchingNextPage={isFetchingNextPage}
					containerHeight="calc(100vh - 100px)"
					selectable
				/>
			) : (
				<div className="px-4">
					<EmptyState margin="2rem 0" label="NO_JOBS" />
				</div>
			)}

			{showCommentModal && (
				<Modal maxWidth="400px" centerContent={false}>
					<FormLabel>Uppdrag #{showCommentModal.orderNumber}</FormLabel>
					<TextArea
						width="100%"
						height="150px"
						ref={commentRef}
						placeholder="PRIVATE_NOTES"
						onChangeText={setUpdatedComment}
						value={updatedComment}
					/>

					<Flex gap="1rem" justifyContent="flex-end" margin="3rem 0 0 0">
						<Button
							backgroundColor="white"
							textColor="grayTextDark"
							onClick={() => setShowCommentModal(undefined)}
						>
							{t("CANCEL")}
						</Button>
						<Button backgroundColor="danger" loading={isUpdatingComment} onClick={updateComment}>
							{t("SAVE")}
						</Button>
					</Flex>
				</Modal>
			)}
		</>
	);
};

export default JobListPage;
